/**
 * CR-6450: supplier-view layout for the Sales Orders → Deliveries filter panel.
 *
 * Regroups the existing filters into the four sections from the supplied
 * design — Date Range · Company · Supplier · Order Details — without changing
 * any control type or filter behaviour. The ticket is explicit that the
 * diagram is AI-generated and may misrepresent input types, and that existing
 * dependent/cascading behaviour must not regress, so every control here is the
 * same element bound to the same handler as the customer layout; only the
 * grouping and order change.
 *
 * Deliberate deviations from the diagram, both to avoid regressing earlier CRs:
 *  - Supplier Code, Commodity Group and Commodity Type are kept (the diagram
 *    omits them; removing working filters would regress the "no regressions"
 *    acceptance criterion). They sit in the section they belong to.
 *  - No Supplier Country control. CR-4887 round 4 deliberately hid supplier
 *    country on supplier-portal pages — a supplier filtering by supplier
 *    country is meaningless when they are the supplier — and the diagram is
 *    titled "Customer View". Raised on the ticket rather than reinstated here.
 */
import { Calendar, Building2, Truck, FileText } from 'lucide-react';
import SearchableSelect from '../../../ui/SearchableSelect';
import {
  inputCls, labelCls, dateWrap, dateInputCls, gridCls,
} from './deliveryFilterStyles';
import { FilterSection } from '../../../filters/FilterSection';

/** Horizontal divider with padding so it doesn't touch card edges */
const SectionDivider = () => (
  <div className="mx-4 border-t border-border" />
);

interface FilterOption {
  value: string;
  label: string;
  group?: number | null;
}

export interface DeliveryFilterSupplierLayoutProps {
  filters: Record<string, string>;
  textInputs: Record<string, string>;
  pendingDates: { dateFrom: string; dateTo: string; oddFrom: string; oddTo: string };
  quickRanges: ReadonlyArray<{ key: string; label: string }>;
  getQuickRangeDates: (key: string) => { dateFrom: string; dateTo: string };
  viewScopedSites: FilterOption[];
  warehouseOptions: Array<{ id: number; name: string }>;
  supplierGroupOptions: FilterOption[];
  supplierOptions: FilterOption[];
  commodityGroupOptions: FilterOption[];
  commodityTypeOptions: FilterOption[];
  onFilterChange: (key: string, value: string) => void;
}

export function DeliveryFilterSupplierLayout({
  filters,
  textInputs,
  pendingDates,
  quickRanges,
  getQuickRangeDates,
  viewScopedSites,
  warehouseOptions,
  supplierGroupOptions,
  supplierOptions,
  commodityGroupOptions,
  commodityTypeOptions,
  onFilterChange,
}: DeliveryFilterSupplierLayoutProps) {
  const dateField = (label: string, key: 'dateFrom' | 'dateTo' | 'oddFrom' | 'oddTo') => (
    <div>
      <label className={labelCls}>{label}</label>
      <div className={dateWrap}>
        <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <input
          type="date"
          value={pendingDates[key]}
          onChange={(e) => onFilterChange(key, e.target.value)}
          className={dateInputCls}
        />
      </div>
    </div>
  );

  const textField = (label: string, key: string, value: string, placeholder: string) => (
    <div>
      <label className={labelCls}>{label}</label>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onFilterChange(key, e.target.value)}
        className={inputCls}
      />
    </div>
  );

  // CR-6442 rework: filter commodity types based on selected commodity group.
  // Same cascade logic as the customer layout — selecting a group narrows the
  // type dropdown to only show types belonging to that group.
  const commodityTypesForSelection = filters['commodityGroup']
    ? commodityTypeOptions.filter((t) => String(t.group ?? '') === filters['commodityGroup'])
    : commodityTypeOptions;

  // CR-7106: one quick-range row per date pair — the same ranges now apply
  // to the ODD pair, which previously offered only raw date inputs.
  const quickRangeRow = (fromKey: 'dateFrom' | 'oddFrom', toKey: 'dateTo' | 'oddTo', rowLabel: string) => (
    <div className="flex flex-wrap items-center gap-2 mb-3">
      <span className="text-xs font-medium text-muted-foreground w-24">{rowLabel}</span>
      {quickRanges.map(({ key, label }) => {
        const dates = getQuickRangeDates(key);
        // CR-6364: reflect the pending (not-yet-applied) selection so a
        // clicked range highlights before it is applied.
        const isActive = pendingDates[fromKey] === dates.dateFrom && pendingDates[toKey] === dates.dateTo;
        return (
          <button
            key={key}
            onClick={() => {
              onFilterChange(fromKey, dates.dateFrom);
              onFilterChange(toKey, dates.dateTo);
            }}
            className={`px-3 py-1.5 text-xs font-medium border rounded-md transition-colors ${
              isActive
                ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                : 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary'
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="[&>section]:border-t-0">
      {/* Divider above first section */}
      <SectionDivider />

      {/* Date Range — the diagram folds the quick ranges and both date pairs
          into one section; they were previously split top and bottom. */}
      <FilterSection title="Date Range" icon={Calendar}>
        {quickRangeRow('dateFrom', 'dateTo', 'Delivery Date:')}
        {quickRangeRow('oddFrom', 'oddTo', 'ODD:')}
        <div className={gridCls}>
          {dateField('Delivery Date From', 'dateFrom')}
          {dateField('Delivery Date To', 'dateTo')}
          {dateField('ODD From', 'oddFrom')}
          {dateField('ODD To', 'oddTo')}
        </div>
      </FilterSection>

      <SectionDivider />

      {/* Company */}
      <FilterSection title="Company" icon={Building2}>
        <div className={gridCls}>
          <div>
            <label className={labelCls}>Company Site</label>
            <SearchableSelect
              options={viewScopedSites}
              value={filters['site'] || ''}
              onChange={(val) => onFilterChange('site', val)}
              placeholder="All Company Sites"
            />
          </div>
          <div>
            <label className={labelCls}>Warehouse</label>
            <select
              value={filters['warehouse'] || ''}
              onChange={(e) => onFilterChange('warehouse', e.target.value)}
              className={inputCls}
            >
              <option value="">All Warehouses</option>
              {warehouseOptions.map((wh) => (
                <option key={wh.id} value={String(wh.id)}>{wh.name}</option>
              ))}
            </select>
          </div>
        </div>
      </FilterSection>

      <SectionDivider />

      {/* Supplier — Supplier Group still narrows Supplier Site via the parent's
          existing onFilterChange cascade; nothing about that logic moves. */}
      <FilterSection title="Supplier" icon={Truck}>
        <div className={gridCls}>
          <div>
            <label className={labelCls}>Supplier Group</label>
            <SearchableSelect
              options={supplierGroupOptions}
              value={filters['supplierGroup'] || ''}
              onChange={(val) => onFilterChange('supplierGroup', val)}
              placeholder="All Supplier Groups"
            />
          </div>
          <div>
            <label className={labelCls}>Supplier Site</label>
            <SearchableSelect
              options={supplierOptions}
              value={filters['supplier'] || ''}
              onChange={(val) => onFilterChange('supplier', val)}
              placeholder="All Supplier Sites"
            />
          </div>
          {textField('Supplier Code', 'supplierCode', filters['supplierCode'] || '', 'Supplier code...')}
        </div>
      </FilterSection>

      <SectionDivider />

      {/* Order Details — the diagram's fourth section merges what were the
          Order, Commodity and Status sections. */}
      <FilterSection title="Order Details" icon={FileText}>
        <div className={gridCls}>
          {textField('PO Number', 'poNumber', textInputs['poNumber'] || '', 'PO Number...')}
          <div>
            <label className={labelCls}>PO Line Status</label>
            <select
              value={filters['poLineStatus'] || ''}
              onChange={(e) => onFilterChange('poLineStatus', e.target.value)}
              className={inputCls}
            >
              <option value="">All</option>
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          {textField('GRN Number', 'grnNumber', textInputs['grnNumber'] || '', 'GRN Number...')}
          {/* The diagram lists "GRN status" and "On-time status" separately, but
              draws GRN status with the Early/Late values — i.e. this control.
              Shipped once, as the existing On-Time Status filter. */}
          <div>
            <label className={labelCls}>On-Time Status</label>
            <select
              value={filters['grnStatus'] || ''}
              onChange={(e) => onFilterChange('grnStatus', e.target.value)}
              className={inputCls}
            >
              <option value="">All</option>
              <option value="on_time">On Time</option>
              <option value="early">Early</option>
              <option value="late">Late</option>
              <option value="early_late">Early/Late</option>
            </select>
          </div>
          {textField('Line Number', 'lineNumber', textInputs['lineNumber'] || '', 'Line number...')}
          {textField('Part Number', 'partNumber', textInputs['partNumber'] || '', 'Part number...')}
          <div>
            <label className={labelCls}>Appeal Status</label>
            <select
              value={filters['appealFilter'] || ''}
              onChange={(e) => onFilterChange('appealFilter', e.target.value)}
              className={inputCls}
            >
              <option value="">All</option>
              <option value="no_appeal">Not Appealed</option>
              <option value="appealed">Appealed</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Commodity Group</label>
            <SearchableSelect
              options={commodityGroupOptions}
              value={filters['commodityGroup'] || ''}
              onChange={(val) => onFilterChange('commodityGroup', val)}
              placeholder="All"
            />
          </div>
          <div>
            <label className={labelCls}>Commodity Type</label>
            <select
              value={filters['commodityType'] || ''}
              onChange={(e) => onFilterChange('commodityType', e.target.value)}
              className={inputCls}
            >
              <option value="">All</option>
              {commodityTypesForSelection.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </FilterSection>
    </div>
  );
}
